
import { useState, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

export default function Contact() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.subject || !formData.message) return;

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const body = new URLSearchParams();
      body.append('name', formData.name);
      body.append('email', formData.email);
      body.append('subject', formData.subject);
      body.append('message', formData.message);

      const res = await fetch('https://readdy.ai/api/form/d6492lb0723ekv2itnbg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      if (res.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-white">
      <div className="max-w-6xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-xs font-semibold tracking-[0.2em] uppercase text-red-600 mb-3 block">
            {t('contact.label')}
          </span>
          <h2 className="text-3xl md:text-4xl font-light text-stone-800 tracking-tight mb-4">
            {t('contact.title')}
          </h2>
          <p className="text-sm text-stone-500 max-w-lg mx-auto leading-relaxed">
            {t('contact.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-start">
          {/* Left - Contact Info */}
          <div className="lg:col-span-2 space-y-8">
            {/* Contact Cards */}
            <div className="space-y-4">
              {/* Email Card */}
              <div className="bg-stone-50 rounded-xl p-5 flex items-start gap-4 hover:shadow-md transition-shadow duration-300">
                <div className="w-11 h-11 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-mail-line text-red-600 text-lg"></i>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-stone-800 mb-1">{t('contact.emailUs')}</h4>
                  <a href="mailto:mysacoze@gmail.com" className="text-sm text-stone-600 hover:text-red-600 transition-colors cursor-pointer">
                    mysacoze@gmail.com
                  </a>
                  <p className="text-xs text-stone-400 mt-1">{t('contact.emailResponse')}</p>
                </div>
              </div>

              {/* Phone Card */}
              <div className="bg-stone-50 rounded-xl p-5 flex items-start gap-4 hover:shadow-md transition-shadow duration-300">
                <div className="w-11 h-11 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-phone-line text-red-600 text-lg"></i>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-stone-800 mb-1">{t('contact.callUs')}</h4>
                  <a href="tel:+905012106588" className="text-sm text-stone-600 hover:text-red-600 transition-colors cursor-pointer">
                    +90 (501) 210 6588
                  </a>
                  <p className="text-xs text-stone-400 mt-1">{t('contact.callHours')}</p>
                </div>
              </div>

              {/* WhatsApp Card */}
              <div className="bg-stone-50 rounded-xl p-5 flex items-start gap-4 hover:shadow-md transition-shadow duration-300">
                <div className="w-11 h-11 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-whatsapp-line text-green-600 text-lg"></i>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-stone-800 mb-1">{t('contact.whatsapp')}</h4>
                  <a
                    href="https://wa.me/905012106588"
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className="text-sm text-stone-600 hover:text-green-600 transition-colors cursor-pointer"
                  >
                    {t('contact.whatsappChat')}
                  </a>
                  <p className="text-xs text-stone-400 mt-1">{t('contact.whatsappResponse')}</p>
                </div>
              </div>

              {/* Location Card */}
              <div className="bg-stone-50 rounded-xl p-5 flex items-start gap-4 hover:shadow-md transition-shadow duration-300">
                <div className="w-11 h-11 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-map-pin-line text-red-600 text-lg"></i>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-stone-800 mb-1">{t('contact.location')}</h4>
                  <p className="text-sm text-stone-600">Antalya, Turkiye</p>
                </div>
              </div>
            </div>

            {/* Social Links */}
            <div>
              <p className="text-xs text-stone-400 mb-3 font-medium uppercase tracking-wider">{t('contact.followUs')}</p>
              <div className="flex gap-3">
                <a href="#" className="w-10 h-10 rounded-full bg-stone-100 flex items-center justify-center hover:bg-red-100 hover:text-red-600 text-stone-500 transition-all duration-300 cursor-pointer">
                  <i className="ri-instagram-line text-lg"></i>
                </a>
                <a href="#" className="w-10 h-10 rounded-full bg-stone-100 flex items-center justify-center hover:bg-red-100 hover:text-red-600 text-stone-500 transition-all duration-300 cursor-pointer">
                  <i className="ri-facebook-fill text-lg"></i>
                </a>
                <a href="#" className="w-10 h-10 rounded-full bg-stone-100 flex items-center justify-center hover:bg-red-100 hover:text-red-600 text-stone-500 transition-all duration-300 cursor-pointer">
                  <i className="ri-twitter-x-line text-lg"></i>
                </a>
              </div>
            </div>
          </div>

          {/* Right - Contact Form */}
          <div className="lg:col-span-3">
            <div className="bg-stone-50 rounded-2xl p-8 md:p-10">
              <h3 className="text-xl font-light text-stone-800 mb-1">{t('contact.formTitle')}</h3>
              <p className="text-xs text-stone-400 mb-8">{t('contact.formSubtitle')}</p>

              {submitStatus === 'success' && (
                <div className="mb-6 bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-check-line text-green-600"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-green-800">{t('contact.successTitle')}</p>
                    <p className="text-xs text-green-600">{t('contact.successDesc')}</p>
                  </div>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-error-warning-line text-red-600"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-red-800">{t('contact.errorTitle')}</p>
                    <p className="text-xs text-red-600">{t('contact.errorDesc')}</p>
                  </div>
                </div>
              )}

              <form
                id="mysa-contact-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="space-y-5"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-medium text-stone-600 mb-1.5">{t('contact.nameLabel')}</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder={t('contact.namePlaceholder')}
                      className="w-full px-4 py-3 bg-white border border-stone-200 rounded-lg text-sm text-stone-800 placeholder:text-stone-300 focus:outline-none focus:border-red-400 focus:ring-1 focus:ring-red-400 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-stone-600 mb-1.5">{t('contact.emailLabel')}</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder={t('contact.emailPlaceholder')}
                      className="w-full px-4 py-3 bg-white border border-stone-200 rounded-lg text-sm text-stone-800 placeholder:text-stone-300 focus:outline-none focus:border-red-400 focus:ring-1 focus:ring-red-400 transition-colors"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-stone-600 mb-1.5">{t('contact.subjectLabel')}</label>
                  <select
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-white border border-stone-200 rounded-lg text-sm text-stone-800 focus:outline-none focus:border-red-400 focus:ring-1 focus:ring-red-400 transition-colors cursor-pointer appearance-none"
                    style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 12 12\'%3E%3Cpath fill=\'%23999\' d=\'M6 8L1 3h10z\'/%3E%3C/svg%3E")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right 16px center' }}
                  >
                    <option value="" disabled>{t('contact.subjectPlaceholder')}</option>
                    <option value="Product Questions">{t('contact.subjectProduct')}</option>
                    <option value="Order Support">{t('contact.subjectOrder')}</option>
                    <option value="Shipping & Delivery">{t('contact.subjectShipping')}</option>
                    <option value="Returns & Exchanges">{t('contact.subjectReturns')}</option>
                    <option value="Other">{t('contact.subjectOther')}</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-stone-600 mb-1.5">{t('contact.messageLabel')}</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={(e) => {
                      if (e.target.value.length <= 500) {
                        handleChange(e);
                      }
                    }}
                    required
                    rows={5}
                    maxLength={500}
                    placeholder={t('contact.messagePlaceholder')}
                    className="w-full px-4 py-3 bg-white border border-stone-200 rounded-lg text-sm text-stone-800 placeholder:text-stone-300 focus:outline-none focus:border-red-400 focus:ring-1 focus:ring-red-400 transition-colors resize-none"
                  />
                  <p className="text-xs text-stone-400 text-right mt-1">
                    {formData.message.length}/500 {t('order.characters')}
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-stone-800 text-white py-3 rounded-full text-sm font-medium hover:bg-stone-700 transition-all duration-300 flex items-center justify-center gap-2 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin w-4 h-4 flex items-center justify-center"></i>
                      {t('contact.sending')}
                    </>
                  ) : (
                    <>
                      {t('contact.sendButton')}
                      <i className="ri-send-plane-line w-4 h-4 flex items-center justify-center"></i>
                    </>
                  )}
                </button>

                <p className="text-xs text-stone-400 text-center flex items-center justify-center gap-1.5">
                  <i className="ri-lock-line w-3 h-3 flex items-center justify-center"></i>
                  {t('contact.privacy')}
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
